import { FileSchema } from '../../domains/deleteFile/actions.types';
import { AzureUploadSessionFields, SharePointUploadSessionFields } from '../../normalization/types';
import { Group, Publisher } from '../types';

export enum UploadStorage {
  Azure = 'Azure',
  SharePoint = 'SharePoint',
}

export interface AzureUploadSession extends Omit<AzureUploadSessionFields, 'sasTokenExpirationTime'> {
  readonly sasTokenExpiresAt: number;
  readonly storage: UploadStorage.Azure;
}

export interface SharePointUploadSession extends SharePointUploadSessionFields {
  readonly storage: UploadStorage.SharePoint;
}

export type UploadSession = AzureUploadSession | SharePointUploadSession;

export type FileUploadJobId = string;

export enum UploadStatus {
  Failed = 'Failed',
  Canceled = 'Canceled',
  Started = 'Started',
  SessionCreated = 'SessionCreated',
  UploadToStorageCompleted = 'UploadToStorageCompleted',
  CompleteSessionStarted = 'CompleteSessionStarted',
  Completed = 'Completed',
}

export interface FileUploadJob {
  readonly id: FileUploadJobId;
  readonly fileName: File['name'];
  readonly fileSize: File['size'];
  readonly bytesUploaded: number;
  readonly status: UploadStatus;
  readonly publisherId: Publisher['id'];
  readonly fileId?: string;
  readonly uploadSession?: UploadSession;
  readonly sharePointFileId?: string;
  readonly abandoned?: boolean;
  readonly schema?: FileSchema;
  readonly smallFileUploadStorageProvider?: UploadStorage;
}

export interface FileUploadJobWithGroupName extends FileUploadJob {
  readonly groupName?: Group['name'];
}

export enum FileUploadJobErrorCode {
  InvalidFileName = 'InvalidFileName',
  NoGroupAccess = 'NoGroupAccess',
  QuotaLimitReached = 'QuotaLimitReached',
  Unknown = 'Unknown',
  DirectMessageFileUploadForbidden = 'DirectMessageFileUploadForbidden',
  VideoUploadMaxDurationExceeded = 'VideoUploadMaxDurationExceeded',
  OneDriveNotProvisioned = 'OneDriveNotProvisioned',
  OneDriveStorageQuotaLimitReached = 'OneDriveStorageQuotaLimitReached',
  MediaFileFormatNotSupported = 'MediaFileFormatNotSupported',
}

export interface FailedFileUploadJob extends FileUploadJob {
  readonly status: UploadStatus.Failed;
  readonly errorCode: FileUploadJobErrorCode;
  readonly errorTimestamp: number;
}

export interface FileUploadJobReference {
  readonly id: FileUploadJobId;
  readonly schema: 'fileUploadJobs';
  readonly editorPlaceholderId?: string;
}

export const isFailedFileUploadJob = (
  uploadJob: FailedFileUploadJob | FileUploadJob
): uploadJob is FailedFileUploadJob => uploadJob.status === UploadStatus.Failed;

export type FileUploadJobs = Record<FileUploadJobId, FileUploadJob | FailedFileUploadJob>;
